export function TermsPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 prose prose-navy">
      <h1 className="text-3xl font-bold text-navy mb-2">Terms &amp; Conditions</h1>
      <p className="text-sm text-navy-500 mb-10">
        Last updated: {new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      <p className="text-navy-700 mb-6">
        These Terms govern your use of TowShare.ca (the "Platform"), operated by Waypoint Labs Inc.
        ("TowShare", "we", "us"). By creating an account, listing a trailer, or booking a rental,
        you agree to these Terms.
      </p>

      <Section title="1. The Platform">
        <p>
          TowShare is an online marketplace that connects trailer owners with renters. We do not
          own the trailers listed and we are not party to the rental agreement between owner and
          renter. Each transaction is between the two users directly.
        </p>
      </Section>

      <Section title="2. Eligibility">
        <ul>
          <li>You must be at least 18 years old.</li>
          <li>You must have a valid Canadian driver's license to rent a trailer.</li>
          <li>You must complete ID verification before your first booking.</li>
          <li>You must have valid auto insurance on the vehicle you'll use to tow.</li>
        </ul>
      </Section>

      <Section title="3. Payments">
        <p>
          All payments are processed by Stripe. TowShare collects a 15% service fee from renters
          and a 10% platform fee from owners. Owners are paid via Stripe Connect within 2–3
          business days after a completed rental.
        </p>
      </Section>

      <Section title="4. Damage deposits">
        <p>
          Every booking includes a refundable damage deposit (the amount is set by the owner and
          shown at checkout). The deposit is pre-authorized on the renter's card via Stripe and
          released 24 hours after the trailer is returned if no claim is filed. If damage is
          reported, TowShare may capture all or part of the deposit toward repair.
        </p>
      </Section>

      <Section title="5. The $1,500 Trailer Guarantee">
        <p>
          TowShare backs every rental with up to <strong>$1,500</strong> of platform-funded
          damage coverage beyond the deposit and the renter's auto insurance. This is not an
          insurance product; it is a discretionary protection program subject to the rules
          published on our Guarantee page.
        </p>
      </Section>

      <Section title="6. Insurance">
        <p>
          Renters are responsible for ensuring their auto insurance covers a trailer in tow.
          TowShare does not sell insurance and is not a licensed insurance broker. In most
          Canadian provinces, a personal auto policy extends to attached trailers — but
          coverage varies and renters are responsible for confirming with their insurer.
        </p>
      </Section>

      <Section title="7. Cancellation">
        <p>
          Renters may cancel free up to 24 hours before pickup. Cancellations within 24 hours
          are subject to a fee equal to one day's rental. Owners who cancel a confirmed booking
          inside 24 hours may have their account suspended.
        </p>
      </Section>

      <Section title="8. Prohibited use">
        <ul>
          <li>Commercial use without the owner's explicit approval.</li>
          <li>Off-road or racetrack use.</li>
          <li>Towing with a vehicle below the trailer's rated capacity.</li>
          <li>Transporting illegal goods.</li>
        </ul>
      </Section>

      <Section title="9. Disputes">
        <p>
          Disputes between owner and renter should first be raised in the booking thread.
          TowShare will mediate if needed. Final determinations are made by TowShare in its
          sole discretion.
        </p>
      </Section>

      <Section title="10. Limitation of liability">
        <p>
          To the maximum extent permitted by law, TowShare's total liability arising out of
          your use of the Platform shall not exceed the greater of (a) the platform fees you
          have paid us in the 12 months preceding the claim, or (b) $1,500.
        </p>
      </Section>

      <Section title="11. Changes to these Terms">
        <p>
          We may update these Terms periodically. Continued use of the Platform after changes
          constitutes acceptance.
        </p>
      </Section>

      <Section title="12. Contact">
        <p>
          Questions? <a className="text-orange hover:underline" href="mailto:hello@towshare.ca">hello@towshare.ca</a>.
          TowShare is operated by Waypoint Labs Inc., Ontario, Canada.
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8 text-navy-700">
      <h2 className="text-xl font-semibold text-navy mb-2">{title}</h2>
      {children}
    </section>
  );
}
